// Sugar Alarm — Cycle 457: a hero's picture.
//
//   PUT    /api/members/{id}/heroes/{hero_id}/avatar        {preset}
//   POST   /api/members/{id}/heroes/{hero_id}/avatar/photo  multipart `image`
//   DELETE /api/members/{id}/heroes/{hero_id}/avatar/photo
//   GET    /api/members/{id}/heroes/{hero_id}/avatar/photo?size=&v=
//
// The photo is served behind auth (never a bucket URL), so a plain <img src>
// cannot load it: `fetchHeroPhotoObjectUrl` fetches the blob with the
// bearer and hands back an object URL, the cycle-105 member-avatar shape.

import { authedFetch, request } from "../lib/apiClient";
import type { ApiError, ApiErrorKind, ApiResult, HeroAvatarPhoto } from "../lib/types";

export interface HeroAvatarOut {
  hero_id: string;
  avatar_preset: string;
  avatar_photo: HeroAvatarPhoto | null;
}

const base = (memberId: string, heroId: string) =>
  `/api/members/${encodeURIComponent(memberId)}/heroes/${encodeURIComponent(heroId)}/avatar`;

export function setPreset(
  memberId: string,
  heroId: string,
  preset: string,
): Promise<ApiResult<HeroAvatarOut>> {
  return request<HeroAvatarOut>(base(memberId, heroId), {
    method: "PUT",
    body: { preset },
  });
}

export function removePhoto(
  memberId: string,
  heroId: string,
): Promise<ApiResult<HeroAvatarOut>> {
  return request<HeroAvatarOut>(`${base(memberId, heroId)}/photo`, { method: "DELETE" });
}

function classify(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 413 || status === 415 || status === 422) return "validation";
  if (status === 429) return "rate_limit";
  if (status >= 500) return "server";
  return "unknown";
}

export async function uploadPhoto(
  memberId: string,
  heroId: string,
  blob: Blob,
  filename = "hero.jpg",
): Promise<ApiResult<HeroAvatarOut>> {
  const form = new FormData();
  form.append("image", blob, filename);
  let res: Response;
  try {
    res = await authedFetch(`${base(memberId, heroId)}/photo`, { method: "POST", body: form });
  } catch (e) {
    return {
      ok: false,
      error: { kind: "network", status: 0, message: e instanceof Error ? e.message : "network error" },
    };
  }
  if (!res.ok) {
    let detail: unknown;
    let message = res.statusText || `HTTP ${res.status}`;
    try {
      const data = await res.json();
      detail = data;
      const d = (data as { detail?: unknown })?.detail;
      if (typeof d === "string") message = d;
    } catch {
      /* not JSON */
    }
    const error: ApiError = { kind: classify(res.status), status: res.status, message, details: detail };
    return { ok: false, error };
  }
  try {
    return { ok: true, data: (await res.json()) as HeroAvatarOut };
  } catch {
    return { ok: false, error: { kind: "unknown", status: res.status, message: "bad response" } };
  }
}

/** The authenticated photo → an object URL, or null on any failure (the
 *  caller falls back to the preset). The caller revokes it. */
export async function fetchHeroPhotoObjectUrl(
  photo: HeroAvatarPhoto,
  size: 128 | 256,
): Promise<string | null> {
  try {
    const sep = photo.url.includes("?") ? "&" : "?";
    const res = await authedFetch(
      `${photo.url}${sep}size=${size}&v=${encodeURIComponent(photo.v)}`,
    );
    if (!res.ok) return null;
    const ctype = res.headers.get("content-type") ?? "";
    if (!ctype.startsWith("image/webp")) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

// ── The words a person sees when an upload is refused ────────────────────

/** Operator copy (2026-10-02) for a HEIC the server cannot decode. */
export const HEIC_NOT_SUPPORTED_LINE =
  "That photo format isn't supported. Try taking the photo in the app instead.";

/** Operator copy (2026-10-02) for a photo over the decode budget (422
 *  `too_many_pixels`) — rare: the app's own uploads are cropped and small. */
export const PHOTO_TOO_LARGE_LINE = "That photo is too large, try a different one.";

/** One plain sentence for every photo-upload refusal code the server sends
 *  (hero photos, member avatars, backgrounds and meal photos alike). */
export function photoUploadErrorLine(err: { status: number; message?: string } | null): string {
  if (!err) return "Couldn't save that photo. Try again.";
  const code = err.message ?? "";
  if (code === "heic_not_supported") return HEIC_NOT_SUPPORTED_LINE;
  if (err.status === 413 || code === "image_too_large") {
    return "That photo is too large. Try a smaller one.";
  }
  if (err.status === 415 || code === "unsupported_image_type" || code === "empty_image") {
    return "That file isn't a photo we can use. Try a JPEG or PNG.";
  }
  if (code === "too_many_pixels") return PHOTO_TOO_LARGE_LINE;
  if (err.status === 422) return "Couldn't read that photo. Try a different one.";
  if (code === "decode_busy") return "Busy processing photos. Try again in a moment.";
  if (err.status === 429) return "Too many photo uploads. Try again in a few minutes.";
  if (err.status === 503) return "Photo uploads aren't available right now. Pick a preset instead.";
  if (err.status === 0) return "You're offline. Check the connection and try again.";
  return "Couldn't save that photo. Try again.";
}
